import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { OneClickPaymentResponse } from 'api/PaymentApi';
import { OrderType } from 'api/TrackingApi';
import { CreditsFunnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import { assertString } from 'helpers/utils/checkType';
import { useCreditPayMutation } from 'hooks/mutations/useCreditPayMutation';
import { useCreditResignMutation } from 'hooks/mutations/useCreditResignMutation';
import { useOneClickPayment } from 'hooks/useOneClickPayment';
import { usePackPrice } from 'hooks/usePackagePrice';
import {
  oneClickPaymentReset,
  setOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/oneClickPaymentSlice';
import {
  getOneClickPaymentOrderId,
  getOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/selectors';
import { addCreditsAmount } from 'store/payment/paymentSlice';
import { getCreditPackageByIdSelector } from 'store/payment/selectors';

import { CvvPopup } from 'components/shared/CvvPopup';
import { DefaultPaymentFailPopup } from 'components/shared/DefaultPaymentFailPopup';
import { InsufficientPaymentFailPopup } from 'components/shared/InsufficientPaymentFailPopup';
import { Payment3dsPopup } from 'components/shared/Payment3dsPopup';

enum PaymentFailPopupType {
  Default = 1,
  Insufficient,
}

interface Props {
  params: {
    funnel: CreditsFunnels;
    packageId: string;
    successEvent?: {
      name: string;
      data?: any;
    };
    redirectOnClosClick?: () => void;
  };
  onClose: () => void;
}

export const CreditsRefillPopup: React.FC<Props> = ({ params, onClose }) => {
  const { t } = useTranslation(['common', 'app']);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const creditPackage = useSelector(
    getCreditPackageByIdSelector(params.packageId)
  );
  const orderId = useSelector(getOneClickPaymentOrderId);
  const verifyUrl = useSelector(getOneClickPaymentVerifyUrl);

  const creditPayMutation = useCreditPayMutation();
  const creditResignMutation = useCreditResignMutation();

  const { cardMask, isProcess, handleRecurrentSubmit, handleResignSubmit } =
    useOneClickPayment({
      recurrentMutation: creditPayMutation,
      resignMutation: creditResignMutation,
    });

  const [isCvvVisible, setIsCvvVisible] = useState(false);
  const [visiblePaymentFailPopup, setVisiblePaymentFailPopup] =
    useState<PaymentFailPopupType | null>(null);
  const shouldReplaceUrlAtUnmount = useRef(true);

  const isCardBlocked = useMemo(
    () => creditPayMutation?.error?.response?.data?.code === 'card_blocked',
    [creditPayMutation]
  );

  const isSubmitting = isProcess;
  const packagePrice = usePackPrice({ package: creditPackage });

  const handleSuccessPayment = () => {
    if (params.successEvent) {
      const successEvent = new CustomEvent(params.successEvent.name, {
        detail: params.successEvent?.data,
      });
      document.dispatchEvent(successEvent);
    }

    onClose();

    if (creditPackage) {
      dispatch(addCreditsAmount(creditPackage?.credits_buy));
    }
  };

  const handlePaymentFail = () => {
    setVisiblePaymentFailPopup(PaymentFailPopupType.Default);
  };

  const handleInsufficientPaymentFail = () => {
    setVisiblePaymentFailPopup(PaymentFailPopupType.Insufficient);
  };

  const handle3dsVerification = (data: OneClickPaymentResponse) => {
    dispatch(setOneClickPaymentVerifyUrl(data.verify_url));
  };

  const handleCloseCvvPopup = () => {
    setIsCvvVisible(false);
  };

  const handleShowCvvPopup = () => {
    setIsCvvVisible(true);
  };

  const handlePay = () => {
    if (creditPackage) {
      handleRecurrentSubmit({
        packageId: creditPackage.id,
        isRefilled: true,
        onSuccessPayment: handleSuccessPayment,
        onRetryablePaymentFail: handleShowCvvPopup,
        on3dsVerifyPaymentFail: handle3dsVerification,
        onInsufficientFundsPaymentFail: handleInsufficientPaymentFail,
        onFinalPaymentFail: handlePaymentFail,
      });
    }
  };

  const handleResign = ({ cvv }: { cvv?: string }) => {
    if (creditPackage?.id && assertString(cvv)) {
      handleResignSubmit({
        packageId: creditPackage.id,
        cvv,
        isRefilled: true,
        onSuccessPayment: handleSuccessPayment,
        on3dsVerifyPaymentFail: handle3dsVerification,
        onInsufficientFundsPaymentFail: handleInsufficientPaymentFail,
        onFinalPaymentFail: handlePaymentFail,
      });
    }
  };

  const handleChangeCreditCard = () => {
    shouldReplaceUrlAtUnmount.current = false;

    history.push(`/payments_credit/${creditPackage?.id}${location.search}`);
    onClose();
  };

  const handlePaymentFailPopupCloseClick = () => {
    if (params.redirectOnClosClick) {
      shouldReplaceUrlAtUnmount.current = false;
      params.redirectOnClosClick();
    }

    onClose();
  };

  useEffect(() => {
    return () => {
      dispatch(oneClickPaymentReset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    history.replace(
      `${location.pathname}?${getFunnelSearchString(params.funnel)}`
    );

    return () => {
      if (shouldReplaceUrlAtUnmount.current) {
        history.replace(location.pathname);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReplaceUrlAtUnmount]);

  useEffect(() => {
    if (creditPackage && cardMask) {
      handlePay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditPackage && cardMask]);

  return (
    <>
      {visiblePaymentFailPopup === PaymentFailPopupType.Default && (
        <DefaultPaymentFailPopup
          buttonText={t('common:tryAnotherCard')}
          onClose={handlePaymentFailPopupCloseClick}
          onContinue={handleChangeCreditCard}
        />
      )}
      {(visiblePaymentFailPopup === PaymentFailPopupType.Insufficient ||
        isCardBlocked) && (
        <InsufficientPaymentFailPopup
          buttonText={t('common:tryAnotherCard')}
          onClose={handlePaymentFailPopupCloseClick}
          onContinue={handleChangeCreditCard}
        />
      )}
      {isCvvVisible && (
        <CvvPopup
          cardMask={cardMask}
          price={`${packagePrice?.applyCurrency(packagePrice?.afterDiscount)}`}
          isSubmitting={isSubmitting}
          onSubmit={handleResign}
          onClose={handleCloseCvvPopup}
        />
      )}
      {!!verifyUrl && !!orderId && (
        <Payment3dsPopup
          url={verifyUrl}
          tracking={{
            enabled: true,
            orderId,
            orderType: OrderType.Credit,
          }}
        />
      )}
    </>
  );
};
